import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { filter, find } from 'ramda';
import Form from '~~elements/Form';
import { TABLET_WIDTH } from '~~elements/Const';
import Input from '~~elements/Input';
import UploadField from '~~elements/UploadField';
import Button from '~~elements/Button';
import DefaultAvatar from '~~elements/static/DefaultAvatar.jpg';
import Select from '~~elements/Select';
import Modal from '~~elements/Modal';
import Loading from '~~elements/Loading';
import { PlayerResource } from '~~apis/resource';
import { AvatarDisplay, ImageWrapper } from '../styled';
import { parsePlayersData } from '../service';
import UTRMultilingual from '../../Multilingual';
import TTMultilingual from '../../../Tournament/TournamentTeam/Multilingual';
import { playerPositionOptions, playerHandOptions } from '../../../Tournament/Const';
import Column from './Column';
import { StyledDiv as ColumnStyledDiv } from './Column';
import moment from 'moment';

const StyledDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .description {
    margin: 2rem 0;
    white-space: pre-line;
    letter-spacing: 2px;
    fint-size: 0.9rem;
    text-align: start;
  }

  input:disabled {
    opacity: 0.8;
  }
`;

const CreatePlayerDiv = styled.div`
  width: 9rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  gap: 1rem;
  padding: 1rem 0;
  align-self: flex-start;
  @media (max-width: ${TABLET_WIDTH}) {
    width: 8rem;
  }
`;

const PlayerDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
  border: 1px solid #eee;
  border-radius: 2px;
  width: 9rem;
  .read {
    align-self: start;
    white-space: normal;
  }
  @media (max-width: ${TABLET_WIDTH}) {
    width: 8rem;
  }
`;

const getPlayerInitState = (registry) => {
  return {
    player_uniqid: '',
    name: '',
    number: '',
    avatar_path: '',
    avatar_url: '',
    birthday: '',
    first_position: '',
    second_position: '',
    p_hand: '',
    b_hand: '',
    columns: registry.player_columns.map((column) => ({
      ...column,
      value: '',
      url: '',
    })),
  };
};

const PlayersSection = ({ teamInRegistry, registry, onChange, apiInProgress, nonOrgMode = true }) => {
  const [avatarRatio, setAvatarRatio] = useState(registry.avatar_ratio_y / registry.avatar_ratio_x);
  const [currentPlayersData, setCurrentPlayersData] = useState(parsePlayersData(teamInRegistry, registry));

  const [showRecentPlayers, setShowRecentPlayers] = useState(false);
  const [filteredRecentPlayers, setFilteredRecentPlayers] = useState(null);
  const [addedFilteredRecentPlayers, setAddedFilteredRecentPlayers] = useState([]);

  useEffect(() => {
    setAvatarRatio(registry.avatar_ratio_y / registry.avatar_ratio_x);
  }, [registry]);

  useEffect(() => {
    setCurrentPlayersData(parsePlayersData(teamInRegistry, registry));
  }, [registry, teamInRegistry]);

  useEffect(() => {
    const originPlayersData = JSON.stringify(parsePlayersData(teamInRegistry, registry));
    if (originPlayersData === JSON.stringify(currentPlayersData)) {
      onChange(null);
      return;
    }

    onChange(currentPlayersData);
    return;
  }, [currentPlayersData, onChange, registry, teamInRegistry]);

  const handleRecentPlayers = () => {
    setShowRecentPlayers(true);
    setFilteredRecentPlayers(null);
    setAddedFilteredRecentPlayers([]);

    PlayerResource.getTeamRecentPlayers({ teamUniqid: teamInRegistry.connected_team.uniqid })
      .then(({ data: recentPlayers }) => {
        const newFilteredRecentPlayers = filter(
          (recentPlayer) => !find((p) => p.name === recentPlayer.name, currentPlayersData.players),
          recentPlayers,
        );
        console.log(newFilteredRecentPlayers);
        setFilteredRecentPlayers(newFilteredRecentPlayers);
      })
      .catch((e) => {
        setFilteredRecentPlayers([]);
      });
  };

  const addFilteredRecentPlayer = (playerIndex) => () => {
    const recentPlayer = filteredRecentPlayers[playerIndex];
    const playerInitState = getPlayerInitState(registry);
    playerInitState.player_uniqid = recentPlayer.uniqid;
    playerInitState.name = recentPlayer.name;
    playerInitState.avatar_path = recentPlayer.avatar_path;
    playerInitState.avatar_url = recentPlayer.avatar_url;
    playerInitState.birthday =
      !recentPlayer.birthday || !moment(recentPlayer.birthday).isValid()
        ? TTMultilingual(`COMMON.NONE`)
        : moment(recentPlayer.birthday).format('YYYY-MM-DD');
    playerInitState.first_position = recentPlayer.first_position;
    playerInitState.second_position = recentPlayer.second_position;
    playerInitState.p_hand = recentPlayer.p_hand;
    playerInitState.b_hand = recentPlayer.b_hand;

    setCurrentPlayersData({
      ...currentPlayersData,
      players: [playerInitState, ...currentPlayersData.players],
    });
    setAddedFilteredRecentPlayers([...addedFilteredRecentPlayers, playerIndex]);
  };

  const closeRecentPlayers = () => {
    setShowRecentPlayers(false);
    setFilteredRecentPlayers(null);
    setAddedFilteredRecentPlayers([]);
  };

  const handleCreatePlayer = () => {
    setCurrentPlayersData({
      ...currentPlayersData,
      players: [getPlayerInitState(registry), ...currentPlayersData.players],
    });
  };

  const handleAvatarUpdate = (playerIndex) => (data) => {
    const removeImagePaths = [...currentPlayersData.remove_image_paths];
    if (currentPlayersData.players[playerIndex].avatar_path) {
      removeImagePaths.push(currentPlayersData.players[playerIndex].avatar_path);
    }

    currentPlayersData.players[playerIndex] = {
      ...currentPlayersData.players[playerIndex],
      avatar_path: data.path,
      avatar_url: data.url,
    };

    setCurrentPlayersData({
      ...currentPlayersData,
      remove_image_paths: [...removeImagePaths],
    });
  };

  const handleRemoveAvatar = (playerIndex) => () => {
    handleAvatarUpdate(playerIndex)({ path: '', url: '' });
  };

  const handleBasicInputOnChange = (playerIndex) => (e) => {
    currentPlayersData.players[playerIndex] = {
      ...currentPlayersData.players[playerIndex],
      [e.target.name]: e.target.value,
    };

    setCurrentPlayersData({
      ...currentPlayersData,
      players: [...currentPlayersData.players],
    });
  };

  const handleColumnOnChange = (playerIndex, columnIndex) => (data) => {
    currentPlayersData.players[playerIndex].columns[columnIndex] = {
      ...currentPlayersData.players[playerIndex].columns[columnIndex],
      value: data.value,
      url: data.url,
    };

    const removeImagePaths = [...currentPlayersData.remove_image_paths];
    if (data.removedImage) {
      removeImagePaths.push(data.removedImage);
    }

    setCurrentPlayersData({
      ...currentPlayersData,
      remove_image_paths: [...removeImagePaths],
    });
  };

  const handleRemovePlayer = (playerIndex) => () => {
    currentPlayersData.players.splice(playerIndex, 1);
    setCurrentPlayersData({
      ...currentPlayersData,
      players: [...currentPlayersData.players],
    });
  };

  return (
    <StyledDiv>
      <Form onSubmit={(e) => e.preventDefault()} className="lg">
        <div className="box">
          <div className="input-field lg">
            <div className="input-list">
              {nonOrgMode && (
                <CreatePlayerDiv>
                  {!!teamInRegistry.connected_team && (
                    <Fragment>
                      <Button mini onClick={handleRecentPlayers} disabled={apiInProgress}>
                        {TTMultilingual(`PRE_TOURN_RECORDED_PLAYERS`)}
                      </Button>
                      <span>- or -</span>
                    </Fragment>
                  )}
                  <Button color="red" onClick={handleCreatePlayer} disabled={apiInProgress}>
                    {TTMultilingual(`COMMON.CREATE`)}
                  </Button>
                </CreatePlayerDiv>
              )}
              {currentPlayersData.players.map((player, playerIndex) => (
                <PlayerDiv key={playerIndex}>
                  {apiInProgress ? (
                    <ImageWrapper avatarRatio={avatarRatio}>
                      <AvatarDisplay url={player.avatar_url} />
                    </ImageWrapper>
                  ) : (
                    <ImageWrapper avatarRatio={avatarRatio}>
                      {player.avatar_path ? (
                        <Fragment>
                          <AvatarDisplay url={player.avatar_url} />
                          <UploadField
                            styleType="box"
                            url="/api/media"
                            customBtn={(onClick) => (
                              <Button width="unset" color="white" onClick={onClick}>
                                {UTRMultilingual(`FORM.REPLACE`)}
                              </Button>
                            )}
                            onChange={handleAvatarUpdate(playerIndex)}
                          />
                          <Button width="unset" color="red" onClick={handleRemoveAvatar(playerIndex)} mini>
                            {UTRMultilingual(`COMMON.REMOVE`)}
                          </Button>
                        </Fragment>
                      ) : (
                        <Fragment>
                          <AvatarDisplay url={DefaultAvatar} />
                          <UploadField
                            styleType="box"
                            url="/api/media"
                            customBtn={(onClick) => (
                              <Button width="unset" color="blue" onClick={onClick}>
                                {UTRMultilingual(`FORM.UPLOAD`)}
                              </Button>
                            )}
                            onChange={handleAvatarUpdate(playerIndex)}
                          />
                        </Fragment>
                      )}
                    </ImageWrapper>
                  )}
                  <ColumnStyledDiv className="s">
                    <label>
                      {TTMultilingual(`COLUMN.NAME`)}
                      <i>*</i>
                    </label>
                    <Input
                      name="name"
                      value={player.name}
                      onChange={handleBasicInputOnChange(playerIndex)}
                      disabled={apiInProgress}
                    />
                  </ColumnStyledDiv>
                  <ColumnStyledDiv className="s">
                    <label>
                      {TTMultilingual(`COLUMN.NUMBER`)}
                      <i>*</i>
                    </label>
                    <Input
                      name="number"
                      value={player.number}
                      onChange={handleBasicInputOnChange(playerIndex)}
                      disabled={apiInProgress}
                    />
                  </ColumnStyledDiv>
                  <ColumnStyledDiv className="s">
                    <label>
                      {TTMultilingual(`COLUMN.BIRTHDAY`)}
                      <i>*</i>
                    </label>
                    <Input
                      type="date"
                      name="birthday"
                      value={player.birthday}
                      onChange={handleBasicInputOnChange(playerIndex)}
                      disabled={apiInProgress}
                    />
                  </ColumnStyledDiv>
                  <ColumnStyledDiv className="s">
                    <label>
                      {TTMultilingual(`COLUMN.FIRST_POSITION`)}
                      <i>*</i>
                    </label>
                    <Select
                      name="first_position"
                      onChange={handleBasicInputOnChange(playerIndex)}
                      value={player.first_position}
                      disabled={apiInProgress}
                    >
                      {playerPositionOptions.map((key) => (
                        <option key={key} value={key}>
                          {TTMultilingual(`POSITION.${key}`)}
                        </option>
                      ))}
                    </Select>
                  </ColumnStyledDiv>
                  <ColumnStyledDiv className="s">
                    <label>
                      {TTMultilingual(`COLUMN.SECOND_POSITION`)}
                      <i>*</i>
                    </label>
                    <Select
                      name="second_position"
                      onChange={handleBasicInputOnChange(playerIndex)}
                      value={player.second_position}
                      disabled={apiInProgress}
                    >
                      {playerPositionOptions.map((key) => (
                        <option key={key} value={key}>
                          {TTMultilingual(`POSITION.${key}`)}
                        </option>
                      ))}
                    </Select>
                  </ColumnStyledDiv>
                  <ColumnStyledDiv className="s">
                    <label>
                      {TTMultilingual(`COLUMN.P_HAND`)}
                      <i>*</i>
                    </label>
                    <Select
                      name="p_hand"
                      onChange={handleBasicInputOnChange(playerIndex)}
                      value={player.p_hand}
                      disabled={apiInProgress}
                    >
                      {playerHandOptions.map((key) => (
                        <option key={key} value={key}>
                          {TTMultilingual(`HAND.${key}`)}
                        </option>
                      ))}
                    </Select>
                  </ColumnStyledDiv>
                  <ColumnStyledDiv className="s">
                    <label>
                      {TTMultilingual(`COLUMN.B_HAND`)}
                      <i>*</i>
                    </label>
                    <Select
                      name="b_hand"
                      onChange={handleBasicInputOnChange(playerIndex)}
                      value={player.b_hand}
                      disabled={apiInProgress}
                    >
                      {playerHandOptions.map((key) => (
                        <option key={key} value={key}>
                          {TTMultilingual(`HAND.${key}`)}
                        </option>
                      ))}
                    </Select>
                  </ColumnStyledDiv>
                  {player.columns.map((column, index) => (
                    <Column
                      className="s"
                      key={index}
                      column={column}
                      onChange={handleColumnOnChange(playerIndex, index)}
                      apiInProgress={apiInProgress}
                    />
                  ))}
                  <br />
                  {nonOrgMode && (
                    <Button
                      color="red"
                      width="unset"
                      mini
                      onClick={handleRemovePlayer(playerIndex)}
                      disabled={apiInProgress}
                    >
                      {TTMultilingual(`COMMON.REMOVE`)}
                    </Button>
                  )}
                </PlayerDiv>
              ))}
            </div>
          </div>
        </div>
      </Form>
      <Modal.Modal size="lg" isOpen={showRecentPlayers} onClickBack={closeRecentPlayers}>
        <Modal.ModalHeader hasBack={true} onClickBack={closeRecentPlayers}>
          {TTMultilingual(`PRE_TOURN_RECORDED_PLAYERS`)}
        </Modal.ModalHeader>
        <Modal.ModalBody>
          <Form onSubmit={(e) => e.preventDefault()} className="lg">
            <div className="box">
              <div className="input-field lg">
                <div className="input-list">
                  {!filteredRecentPlayers ? (
                    <Loading width="10rem" />
                  ) : filteredRecentPlayers.length === 0 ? (
                    TTMultilingual(`NO_RECORD`)
                  ) : (
                    filteredRecentPlayers.map((recentPlayer, index) => (
                      <PlayerDiv key={index}>
                        <ImageWrapper avatarRatio={avatarRatio}>
                          <AvatarDisplay url={!recentPlayer.avatar_url ? DefaultAvatar : recentPlayer.avatar_url} />
                        </ImageWrapper>
                        {addedFilteredRecentPlayers.includes(index) ? (
                          <label>
                            <i className="info">{TTMultilingual(`SUCCESS.ADD`)}</i>
                          </label>
                        ) : (
                          <Button mini color="red" width="unset" onClick={addFilteredRecentPlayer(index)}>
                            {TTMultilingual(`COMMON.ADD`)}
                          </Button>
                        )}
                        <label className="read">{recentPlayer.name}</label>
                        <label className="read">{`${TTMultilingual(`COLUMN.BIRTHDAY`)}: ${
                          !recentPlayer.birthday || !moment(recentPlayer.birthday).isValid()
                            ? TTMultilingual(`COMMON.NONE`)
                            : moment(recentPlayer.birthday).format('YYYY/MM/DD')
                        }`}</label>
                        <label className="read">{`${TTMultilingual(`COLUMN.FIRST_POSITION`)}: ${
                          !recentPlayer.first_position
                            ? TTMultilingual(`COMMON.NONE`)
                            : TTMultilingual(`POSITION.${recentPlayer.first_position}`)
                        }`}</label>
                        <label className="read">{`${TTMultilingual(`COLUMN.SECOND_POSITION`)}: ${
                          !recentPlayer.second_position
                            ? TTMultilingual(`COMMON.NONE`)
                            : TTMultilingual(`POSITION.${recentPlayer.second_position}`)
                        }`}</label>
                        <label className="read">{`${TTMultilingual(`COLUMN.P_HAND`)}: ${
                          !recentPlayer.p_hand
                            ? TTMultilingual(`COMMON.NONE`)
                            : TTMultilingual(`HAND.${recentPlayer.p_hand}`)
                        }`}</label>
                        <label className="read">{`${TTMultilingual(`COLUMN.B_HAND`)}: ${
                          !recentPlayer.b_hand
                            ? TTMultilingual(`COMMON.NONE`)
                            : TTMultilingual(`HAND.${recentPlayer.b_hand}`)
                        }`}</label>
                        <label className="read">{TTMultilingual(`RECENT_RECORD`)}:</label>
                        {recentPlayer.experiences.map((experience, ei) => (
                          <label key={ei} className="read">
                            {experience.season}
                          </label>
                        ))}
                      </PlayerDiv>
                    ))
                  )}
                </div>
              </div>
            </div>
          </Form>
        </Modal.ModalBody>
        <Modal.ModalFooter>
          <Button width="unset" color="transparentWithBorder" onClick={closeRecentPlayers}>
            {TTMultilingual(`COMMON.CLOSE`)}
          </Button>
        </Modal.ModalFooter>
      </Modal.Modal>
    </StyledDiv>
  );
};

export default PlayersSection;
